import Button, { ButtonProps } from '@pelckmans/elementis/components/button';
import ButtonDropdown from '@pelckmans/elementis/components/button-dropdown';
import MaterialWordListIcon from '@pelckmans/elementis/icons/material-wordlist';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { getMedialinkById, isMiniSite } from '../../../../../api/medialink';
import { ANALYTICS_EVENT_SUBLOCATIONS, ANALYTICS_EVENT_TYPES } from '../../../../../constants/analyticsEvents';
import ModalContext from '../../../../../contexts/ModalContext';
import useAnalytics from '../../../../../hooks/useAnalytics';
import useModuleLicenseCheck from '../../../../../hooks/useModuleLicenseCheck';
import useThrowAsyncError from '../../../../../hooks/useThrowAsyncError';
import AppModal from '../../../../../shared-components/AppModal';
import NotLicensedAppModal from '../../../../../shared-components/NotLicensedAppModal';
import AppsContext from '../../../_contexts/AppsContext';
import ModuleContext from '../../../_contexts/ModuleContext';
import { App, isAppWithLinkedApp, isAppWithPlatformUrl, LinkAppTypes, PlatformTypes } from '../../../api/appsApi';
import { isDigibookApp, isOldDigibookApp } from '../../../utils/digibookAppUtils';
import useMiniSiteMedialink from '../../hooks/medialink/useMiniSiteMedialink';

export default function AppQuickActionButton({ size = 'md' }: { size?: ButtonProps['size'] }) {
  const { t } = useTranslation();
  const { apps } = useContext(AppsContext);
  const handleError = useThrowAsyncError();
  const { module } = useContext(ModuleContext);
  const { showModal } = useContext(ModalContext);
  const { openMiniSiteMedialink } = useMiniSiteMedialink();
  const analytics = useAnalytics();

  const handleClick = async (app: App) => {
    if (!module) return;

    await analytics.capture({
      objectId: app.id,
      eventType: ANALYTICS_EVENT_TYPES.APP_ACCESSED,
      subLocationId: module.isExerciseModule
        ? ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_EXERCISE_MODULE_MATERIAL
        : ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_MATERIAL,
    });

    if (app.platformType === PlatformTypes.WEB_INTERN && isAppWithPlatformUrl(app)) {
      return showModal(<AppModal id="app-modal" app={app} />);
    }

    if (app.platformType === PlatformTypes.P_INTERN && isAppWithLinkedApp(app)) {
      if (app.platformLinkType === LinkAppTypes.WORDLIST) {
        try {
          const medialink = await getMedialinkById(app.moduleId, app.linkedApp.id);

          if (isMiniSite(medialink)) {
            await analytics.capture({
              eventType: ANALYTICS_EVENT_TYPES.MEDIALINK_ACCESSED,
              objectId: medialink.id,
              subLocationId: 'portaal-material-apps',
            });

            const modal = await openMiniSiteMedialink(medialink, module);

            if (modal) {
              return showModal(modal);
            }
          }
        } catch (error) {
          handleError(error);
        }
      }
    }

    if (app.platformType === PlatformTypes.WEB_EXTERN && isAppWithPlatformUrl(app)) {
      window.open(app.platformUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    return showModal(<NotLicensedAppModal id="not-licensed-app-modal" appTypeId={app.appType.id} />);
  };

  const { isUserLicensed } = useModuleLicenseCheck();

  if (!module) return null;

  // Not showing apps that are already shown as buttons in the WordListAppButton or DigibookButton
  const appsAsButton = apps.filter(
    app =>
      app.showAsButton &&
      !(app.platformType === PlatformTypes.P_INTERN && app.platformLinkType === LinkAppTypes.WORDLIST) &&
      !isDigibookApp(app) &&
      !isOldDigibookApp(app),
  );

  if (appsAsButton.length === 0) return null;
  if (appsAsButton.length === 1) {
    const singleApp = appsAsButton[0];
    return (
      <Button
        key="app-quick-action-button"
        startIcon={<MaterialWordListIcon data-testid="app-quick-action-icon" />}
        prominence="secondary"
        size={size}
        onClick={() => handleClick(singleApp)}
        disabled={!isUserLicensed}
        tooltip={!isUserLicensed && t('PAGES.TOC_LEVEL.APPS.QUICK_ACTION.NO_LICENSE_TOOLTIP')}
      >
        {singleApp.linkedApp?.manualIncluded
          ? t('PAGES.TOC_LEVEL.APPS.QUICK_ACTION.OPEN_QUICK_ACTION_WITH_MANUAL', { appName: singleApp.name })
          : t('PAGES.TOC_LEVEL.APPS.QUICK_ACTION.OPEN_QUICK_ACTION', { appName: singleApp.name })}
      </Button>
    );
  }

  return (
    <ButtonDropdown
      title={t('PAGES.TOC_LEVEL.APPS.QUICK_ACTION.OPEN_QUICK_ACTION_MULTIPLE')}
      key="app-quick-action-button"
      buttonProps={{
        prominence: 'secondary',
        startIcon: <MaterialWordListIcon data-testid="app-quick-action-icon" />,
        disabled: !isUserLicensed,
      }}
      size={size}
      tooltip={!isUserLicensed && t('PAGES.TOC_LEVEL.APPS.QUICK_ACTION.NO_LICENSE_TOOLTIP')}
      disableBackgroundScroll
    >
      {appsAsButton.map(appAsButton => {
        const isPreview = appAsButton.state === 'preview';
        const disabledProps = isPreview
          ? ({ disabled: true, tooltip: t('PAGES.TOC_LEVEL.APPS.QUICK_ACTION.PREVIEW_TOOLTIP') } as const)
          : {};
        const subText = appAsButton.linkedApp?.manualIncluded
          ? t('PAGES.TOC_LEVEL.APPS.DIGIBOOK.MANUAL_INCLUDED')
          : undefined;

        return (
          <ButtonDropdown.Item
            as="button"
            key={appAsButton.id}
            value={appAsButton.linkedApp?.id}
            text={appAsButton.name}
            subText={subText}
            textIcon={<MaterialWordListIcon data-testid={`app-quick-action-icon-${appAsButton.id}`} />}
            onClick={() => handleClick(appAsButton)}
            {...disabledProps}
          />
        );
      })}
    </ButtonDropdown>
  );
}
